import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Zap, GitBranch, Target, Radio, Send, PlayCircle } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Section from "@/components/Section";
import AuditCta from "@/components/AuditCta";
import DemoModal from "@/components/DemoModal";
import StandardSeal from "@/components/StandardSeal";
import CinematicShell from "@/components/cinematic/CinematicShell";
import { HomeArsenalStill, HomeFlywheelStill, HomeSealStill } from "@/components/cinematic/stills";
import api from "@/lib/api";

const MODULE_ICONS = {
  D1: <Target size={20} strokeWidth={1.5} />,
  D2: <ShieldCheck size={20} strokeWidth={1.5} />,
  D3: <GitBranch size={20} strokeWidth={1.5} />,
  D4: <Zap size={20} strokeWidth={1.5} />,
  D5: <Radio size={20} strokeWidth={1.5} />,
  D6: <Send size={20} strokeWidth={1.5} />,
};

export default function HomePage() {
  const [modules, setModules] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [demoOpen, setDemoOpen] = useState(false);
  useEffect(() => {
    api.get("/config/modules").then((r) => setModules(r.data)).catch(() => setModules([]));
    api.get("/config/faqs").then((r) => setFaqs(r.data)).catch(() => setFaqs([]));
  }, []);

  return (
    <>
      <PublicNav />

      {/* Hero — cinematic Act 1 backdrop on capable devices; falls back to
          the existing scanline/flag-wave DOM treatment on STATIC.  Copy is
          DOM at every tier so LCP is the headline, never a 3D asset. */}
      <div className="relative scanline-overlay dt6-flag-wave dt6-cinematic-vignette" style={{ minHeight: "680px" }}>
        <CinematicShell
          sceneImport={() => import("@/components/cinematic/scenes/HomeArsenal")}
          stillSrc={HomeArsenalStill}
          testId="home-hero-scene"
          className="dt6-cinematic-grain"
          style={{ position: "absolute", inset: 0, zIndex: 0 }}
        />
        <div className="absolute inset-0 pointer-events-none z-[1]" style={{ background: "radial-gradient(1200px 600px at 20% 0%, rgba(178,34,52,0.14), transparent 70%)" }} />
        <Section className="relative pt-20 pb-16 dt6-cinematic-copy" testId="home-hero">
          <div className="max-w-4xl">
            <div className="dt6-chip dt6-chip-primary mb-6 dt6-fade-in" data-testid="hero-chip">/ AMERICA FIRST · AMERICA ONLY</div>
            <h1 className="font-heading font-black text-6xl md:text-7xl leading-[1.02] tracking-tighter dt6-fade-in" data-testid="hero-headline" style={{ animationDelay: "60ms" }}>
              America First.<br />
              <span style={{ color: "var(--dt6-primary)" }}>America Only.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[color:var(--dt6-muted)] max-w-2xl dt6-fade-in" style={{ animationDelay: "140ms" }}>
              Six weapons. One stack. One flag. DT6 is the data engine for vetted America First campaigns and organizations.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 dt6-fade-in" style={{ animationDelay: "220ms" }}>
              <Link to="/alignment/apply" className="dt6-btn dt6-btn-primary" data-testid="hero-apply-cta">
                Apply to be armed <ArrowRight size={16} />
              </Link>
              <Link to="/audit" className="dt6-btn" data-testid="hero-audit-cta">
                Get your free file audit
              </Link>
              <button
                type="button"
                onClick={() => setDemoOpen(true)}
                className="dt6-btn"
                data-testid="hero-try-six-cta"
              >
                <PlayCircle size={16} /> Try SIX live
              </button>
            </div>
            <div className="mt-10 flex items-center gap-6 font-mono text-[0.72rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)] dt6-fade-in" style={{ animationDelay: "320ms" }}>
              <span>· VETTED ORGS ONLY</span>
              <span>· TCPA + STATE-LAW POSTURE</span>
              <span>· AUTO-DELIVERY BY SIX</span>
            </div>
          </div>
        </Section>
      </div>

      {/* Modules grid */}
      <Section className="pt-8 pb-16" testId="home-modules">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="dt6-chip mb-3">/ THE STACK</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold">Six modules, one operating system.</h2>
          </div>
          <Link to="/platform" className="hidden md:inline-flex dt6-btn" data-testid="modules-see-more">
            How the stack works <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map((m, i) => (
            <Link
              key={m.code}
              to={`/modules/${m.code}`}
              className="dt6-card p-6 group hover:border-[color:var(--dt6-primary)] transition-colors duration-150 dt6-fade-in"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`home-module-card-${m.code}`}
            >
              <div className="flex items-start justify-between">
                <div className="text-[color:var(--dt6-primary)]">{MODULE_ICONS[m.code] || null}</div>
                <div className="font-mono text-[0.7rem] tracking-[0.18em] text-[color:var(--dt6-muted)] group-hover:text-[color:var(--dt6-primary)]">
                  {m.code}
                </div>
              </div>
              <h3 className="mt-6 font-heading text-2xl font-bold">{m.name}</h3>
              <p className="mt-2 text-sm text-[color:var(--dt6-muted)]">{m.tagline}</p>
              <div className="mt-4 pt-4 border-t flex items-center justify-between text-xs" style={{ borderColor: "var(--dt6-border)" }}>
                <span className="text-[color:var(--dt6-muted)]">{m.deliverables?.length || 0} deliverables</span>
                <span className="text-[color:var(--dt6-primary)] font-mono">→</span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* Standard strip — declaration first sentence + seal + link to /standard */}
      <Section className="pt-4 pb-8 relative" testId="home-standard-strip">
        <CinematicShell
          sceneImport={() => import("@/components/cinematic/scenes/HomeSeal")}
          stillSrc={HomeSealStill}
          testId="home-standard-scene"
          style={{ position: "absolute", inset: 0, zIndex: 0, opacity: 0.55, pointerEvents: "none" }}
        />
        <Link to="/standard" className="dt6-card p-6 md:p-8 flex flex-col sm:flex-row items-center gap-6 hover:border-[color:var(--dt6-primary)] transition-colors relative z-[2] dt6-cinematic-copy" style={{ borderColor: "var(--dt6-primary)", background: "rgba(26,31,42,0.72)", backdropFilter: "blur(8px)" }} data-testid="home-standard-link">
          <div className="flex-shrink-0"><StandardSeal size={92} /></div>
          <div className="flex-1">
            <div className="font-mono text-xs text-[color:var(--dt6-primary)] mb-2">/ THE DT6 STANDARD</div>
            <p className="text-sm sm:text-base leading-relaxed text-[color:var(--dt6-text)]">
              DT6 arms America First leaders and organizations. No exceptions, no dual loyalties, no strategic neutrality.
            </p>
          </div>
          <ArrowRight size={18} className="text-[color:var(--dt6-primary)]" />
        </Link>
      </Section>

      {/* Flywheel — cinematic Act 2/3 backdrop */}
      <Section className="py-16 relative" testId="home-flywheel">
        <CinematicShell
          sceneImport={() => import("@/components/cinematic/scenes/HomeFlywheel")}
          stillSrc={HomeFlywheelStill}
          testId="home-flywheel-scene"
          style={{ position: "absolute", inset: 0, zIndex: 0, opacity: 0.6, pointerEvents: "none" }}
        />
        <div className="dt6-card p-8 md:p-12 relative z-[2] dt6-cinematic-copy" style={{ background: "linear-gradient(180deg, rgba(26,31,42,0.85), rgba(26,31,42,0.65))", backdropFilter: "blur(6px)" }}>
          <div className="dt6-chip mb-4">/ HOW THE STACK WORKS</div>
          <h3 className="font-heading text-3xl md:text-4xl font-bold max-w-3xl">
            One universe in. Six clean signals out. A repeatable outreach engine.
          </h3>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-6 gap-3">
            {["Source","Clean","Identify","Score","Route","Activate"].map((step, i) => (
              <div key={step} className="dt6-card p-4">
                <div className="font-mono text-[0.7rem] tracking-[0.14em] text-[color:var(--dt6-muted)]">D{i+1}</div>
                <div className="font-heading text-lg font-bold mt-2">{step}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Who we arm */}
      <Section className="py-16" testId="home-who-we-arm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="dt6-chip mb-4">/ WHO WE ARM</div>
            <h3 className="font-heading text-3xl md:text-4xl font-bold">
              Built for the operators moving the number.
            </h3>
            <p className="mt-4 text-[color:var(--dt6-muted)]">
              DT6 is used by federal, statewide, and legislative campaigns; independent-expenditure PACs;
              party committees; and issue movements aligned with the America-First agenda. We qualify
              every engagement before beginning work.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              "Federal + Senate campaigns",
              "Statewide + gubernatorial",
              "House + legislative",
              "Independent-expenditure PACs",
              "Party committees",
              "Issue + movement orgs",
              "America-First consultants",
              "Grassroots operations",
            ].map((label) => (
              <div key={label} className="dt6-card px-4 py-3 text-sm">{label}</div>
            ))}
          </div>
        </div>
      </Section>

      {/* Compliance strip */}
      <Section className="py-10" testId="home-compliance">
        <div className="dt6-card p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="font-mono text-[0.7rem] tracking-[0.14em] text-[color:var(--dt6-muted)]">/ COMPLIANCE POSTURE</div>
            <p className="mt-2 text-lg font-heading font-semibold">
              Consent-first capture. TCPA cell-flag pass. CAN-SPAM adherence. State-privacy aware. IE/CE separation.
            </p>
          </div>
          <Link to="/compliance" className="dt6-btn" data-testid="home-compliance-link">
            Read the posture <ArrowRight size={14} />
          </Link>
        </div>
      </Section>

      {/* FAQs */}
      {faqs.length > 0 && (
        <Section className="py-16" testId="home-faqs">
          <div className="dt6-chip mb-4">/ FIELD MANUAL</div>
          <h3 className="font-heading text-3xl md:text-4xl font-bold mb-8">Frequently asked.</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {faqs.map((f) => (
              <div key={f.question} className="dt6-card p-6" data-testid={`faq-${f.display_order}`}>
                <div className="font-heading font-bold text-lg">{f.question}</div>
                <p className="mt-2 text-sm text-[color:var(--dt6-muted)]">{f.answer}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section className="pb-16" testId="home-audit-cta-block">
        <AuditCta context="home-audit-cta" />
      </Section>

      <PublicFooter />
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </>
  );
}
